import { memo } from "react";
import EnterFullScreenIcon from "~icons/local/enter-full-screen";
import ExitFullScreenIcon from "~icons/local/exit-full-screen";
import useModal from "@/lib/modal/useModal.js";
import PartThumbnail from "@/lib/PartThumbnail";
import classnames from "classnames";
import { useParams } from "react-router";
import { getUrl } from "@/lib/useToPage";
import StyledLink from "@/lib/StyledLink";
import { components } from "@/lib/ApiSchema";

export default function EngineerDrawing({
  hide,
  show,
  url,
  thumbnailUrl,
  parentPartTD,
}: {
  hide?: () => void;
  show: boolean;
  url?: string;
  thumbnailUrl?: string | null;
  parentPartTD?: components["schemas"]["ParentTechnicalDrawingSchema"] | null;
}) {
  const { teamSlug, rfqId } = useParams();
  const { showModal, closeModal } = useModal();

  return (
    <div className={`w-full h-full relative ${show ? "" : "hidden"}`}>
      {url ? (
        <>
          <EnterFullScreenIcon
            className={classnames("absolute cursor-pointer left-3", {
              "top-14": parentPartTD,
              "top-3": !parentPartTD,
            })}
            onClick={() =>
              showModal(
                <div className="w-screen h-screen">
                  <ExitFullScreenIcon
                    className="fixed top-7 left-7 cursor-pointer"
                    onClick={closeModal}
                  />
                  <Iframe url={url} />
                </div>,
              )
            }
          />
          {parentPartTD && (
            <div className="bg-red-50 px-2 py-1 text-xs">
              Showing parent part's engineering drawing
              <br />(
              <StyledLink
                to={getUrl("part", {
                  teamSlug: teamSlug!,
                  rfqId: rfqId!,
                  partId: parentPartTD.part_id,
                })}
              >
                {parentPartTD.part_number}
              </StyledLink>
              ), as there is no unique drawing for this part
            </div>
          )}
          <Iframe url={url} />
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-start">
          <div className="p-8 text-center">
            No engineering drawing (.pdf) file for this part.
          </div>
        </div>
      )}
      {hide && (
        <PartThumbnail
          url={thumbnailUrl}
          className="!absolute cursor-pointer bottom-4 right-4 size-14 border shadow-xl"
          onClick={hide}
        />
      )}
    </div>
  );
}

const Iframe = memo(
  function Iframe({ url }: { url: string }) {
    return (
      <iframe
        src={`${url}#navpanes=0&view=FitH&zoom=page-fit`}
        className="w-full h-full"
      />
    );
  },
  (prevProps, nextProps) => {
    const prevUrl = new URL(prevProps.url);
    const nextUrl = new URL(nextProps.url);

    // intentionally exclude search params to prevent unnecessary re-reloads
    return (
      prevUrl.host === nextUrl.host && prevUrl.pathname === nextUrl.pathname
    );
  },
);
